import { getBackendClient } from "@/lib/backend";
import { firstRecord, toNumber } from "@/lib/utils/query-helpers";

/**
 * Calls a `*_list_totals` RPC and reads its single aggregate row.
 *
 * Every list table asks the same question of the server — what does the whole
 * filtered set add up to — and each answer arrives the same way: one row of
 * numerics, which PostgREST may render as strings. This is that call, once.
 *
 * The filter arguments are the caller's to get right: a totals RPC takes
 * exactly the filters its list query takes, and passing a different set
 * produces a footer that contradicts the rows above it.
 */
export async function fetchListTotals<TKey extends string>(
  rpcName: string,
  args: Record<string, unknown>,
  fields: readonly TKey[],
): Promise<Record<TKey, number>> {
  const { data, error } = await getBackendClient().data.rpc(rpcName, args);

  if (error) {
    throw new Error(error.message);
  }

  const row = firstRecord(data);
  if (!row) {
    throw new Error(`${rpcName} returned no aggregate row.`);
  }

  const totals = {} as Record<TKey, number>;
  for (const field of fields) {
    // A missing or unparseable aggregate is a contract break, not a zero:
    // reporting 0 for "we could not read the total" is the exact failure this
    // whole path exists to prevent.
    const value = toNumber(row[field]);
    if (value === null) {
      throw new Error(`${rpcName} returned no numeric ${field}.`);
    }
    totals[field] = value;
  }

  return totals;
}

/** Aggregate shape shared by the three money documents. */
export const DOCUMENT_TOTAL_FIELDS = [
  "row_count",
  "total_amount",
  "total_paid",
  "balance",
] as const;

export type DocumentListTotals = Record<(typeof DOCUMENT_TOTAL_FIELDS)[number], number>;
